import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from 'core/i18n/I18nProvider';

const ConfirmContext = createContext(null);

function getFocusable(root) {
  if (!root) {
    return [];
  }
  return Array.from(root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
    .filter((node) => !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true');
}

export function ConfirmProvider({ children }) {
  const { t } = useI18n();
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const previousFocusRef = useRef(null);

  const confirm = useCallback((options = {}) => new Promise((resolve) => {
    previousFocusRef.current = document.activeElement;
    resolverRef.current = resolve;
    setDialog({
      title: options.title || t('confirm.title'),
      body: options.body || '',
      confirmLabel: options.confirmLabel || t('actions.confirm'),
      cancelLabel: options.cancelLabel || t('actions.cancel'),
      tone: options.tone || 'danger',
      action: options.action || null,
    });
  }), [t]);

  const close = useCallback(async (result) => {
    const resolve = resolverRef.current;
    const action = dialog?.action;
    resolverRef.current = null;
    setDialog(null);
    if (result && typeof action === 'function') {
      await action();
    }
    const previous = previousFocusRef.current;
    previousFocusRef.current = null;
    if (resolve) {
      resolve(result);
    }
    if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
      previous.focus();
    }
  }, [dialog]);

  useEffect(() => {
    if (!dialog) {
      return undefined;
    }
    cancelRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(false);
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusable = getFocusable(dialogRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close, dialog]);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {dialog ? (
        <div className="modal-backdrop" data-testid="confirm-backdrop" role="presentation">
          <div
            ref={dialogRef}
            className="modal modal-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby="confirm-body"
          >
            <h2 id="confirm-title">{dialog.title}</h2>
            <p id="confirm-body">{dialog.body}</p>
            <div className="modal-actions">
              <button ref={cancelRef} type="button" className="btn btn-ghost" onClick={() => { void close(false); }}>
                {dialog.cancelLabel}
              </button>
              <button type="button" className={`btn btn-${dialog.tone}`} onClick={() => { void close(true); }}>
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return context.confirm;
}
